import { Button, Dropdown, Input, Modal, Space, Spin, Table, Tag } from 'antd';
import type { MenuProps } from 'antd';
import type { ColumnType } from 'antd/es/table';
import React, { useEffect, useMemo, useState } from 'react';
import { DownOutlined, SearchOutlined } from '@ant-design/icons';

import { Category, Document } from '../../interfaces/documents';
import { ContractorApiListSharedType, ShareItemRecord } from '../../types';
import { useApi } from '../../api/useApi';
import { getListDataAfterShareItemsDelete } from './get-list-data-after-share-items-delete';
import { getRecipientContact } from './get-recipient-contact';

interface MySharedDocumentsTabManagerProps {
    listData: ContractorApiListSharedType;
    setUpdateData: React.Dispatch<React.SetStateAction<boolean>>;
}

type StopShareType = 'recipient' | 'resource' | 'recipient-resource';

type DocumentsApiWithManagerStopShare = {
    stopShare(payload: {
        typeStop: StopShareType;
        recipientId?: string;
        resourceId?: string;
        targetSharerId?: string;
    }): Promise<Response>;
};

type ManagerShareRow = {
    key: string;
    sharerId: string;
    sharerContact: string;
    recipientId: string;
    recipientContact: string;
    resourceId: string;
    resourceName: string;
    resourceType: 'Document' | 'Folder';
    createdAt: number;
    dueDate: number;
};

type TableFilterOption = NonNullable<ColumnType<ManagerShareRow>['filters']>[number];

type StopShareConfig = {
    title: string;
    description: string;
    recipientId?: string;
    resourceId?: string;
};

type PendingStopShareAction = StopShareConfig & {
    typeStop: StopShareType;
    row: ManagerShareRow;
};

const getDocumentName = (document: Document | undefined, fallbackId: string) => {
    if (!document) {
        return fallbackId;
    }

    return [document.name, document.extension].filter(Boolean).join(' ');
};

const formatEpochSeconds = (value: number) => {
    if (!value) {
        return '';
    }

    return new Date(value * 1000).toLocaleString();
};

const buildManagerRows = (listData: ContractorApiListSharedType): ManagerShareRow[] => {
    const shareById = new Map(listData.listShare.map((share) => [share.id, share]));
    const recipientById = new Map(listData.listRecipient.map((recipient) => [recipient.id, recipient]));
    const sharerById = new Map((listData.listSharer ?? []).map((sharer) => [sharer.id, sharer]));
    const documentById = new Map(listData.listDocument.map((document) => [document.id, document]));
    const folderById = new Map(listData.listFolder.map((folder) => [folder.uuid, folder]));

    return listData.listShareItem
        .flatMap((shareItem) => {
            const share = shareById.get(shareItem.shareId);

            if (!share) {
                return [];
            }

            const sharer = sharerById.get(shareItem.sid);
            const recipient = recipientById.get(shareItem.rid);
            const folder = folderById.get(shareItem.id);
            const document = documentById.get(shareItem.id);
            const isFolder = shareItem.t === 'fld';
            const resourceType: ManagerShareRow['resourceType'] = isFolder ? 'Folder' : 'Document';
            const resourceName = isFolder
                ? ((folder as Category | undefined)?.name ?? shareItem.id)
                : getDocumentName(document as Document | undefined, shareItem.id);

            return [
                {
                    key: `${shareItem.shareId}:${shareItem.id}`,
                    sharerId: shareItem.sid,
                    sharerContact: sharer ? getRecipientContact(sharer) : shareItem.sid,
                    recipientId: shareItem.rid,
                    recipientContact: recipient ? getRecipientContact(recipient) : shareItem.rid,
                    resourceId: shareItem.id,
                    resourceName,
                    resourceType,
                    createdAt: share.createdAt || shareItem.cat,
                    dueDate: share.dueDate,
                },
            ];
        })
        .sort((a, b) => b.createdAt - a.createdAt);
};

const buildFilterOptions = (
    rows: ManagerShareRow[],
    getValue: (row: ManagerShareRow) => string,
    getText: (row: ManagerShareRow) => string,
): TableFilterOption[] => {
    const optionByValue = new Map<string, TableFilterOption>();

    rows.forEach((row) => {
        const value = getValue(row);

        if (!optionByValue.has(value)) {
            optionByValue.set(value, {
                text: getText(row),
                value,
            });
        }
    });

    return Array.from(optionByValue.values()).sort((a, b) => String(a.text).localeCompare(String(b.text)));
};

export const MySharedDocumentsTabManager: React.FC<MySharedDocumentsTabManagerProps> = ({
    listData,
    setUpdateData,
}) => {
    const [isLoading, setIsLoading] = useState(false);
    const [internalListData, setInternalListData] = useState<ContractorApiListSharedType>(listData);
    const [pendingStopShareAction, setPendingStopShareAction] = useState<PendingStopShareAction | null>(null);

    const api = useApi();
    const documentsApi = api.documents as DocumentsApiWithManagerStopShare;

    useEffect(() => {
        setInternalListData(listData);
    }, [listData]);

    const rows = useMemo(() => buildManagerRows(internalListData), [internalListData]);

    const sharerFilters = useMemo(
        () =>
            buildFilterOptions(
                rows,
                (row) => row.sharerId,
                (row) => row.sharerContact,
            ),
        [rows],
    );
    const recipientFilters = useMemo(
        () =>
            buildFilterOptions(
                rows,
                (row) => row.recipientId,
                (row) => row.recipientContact,
            ),
        [rows],
    );

    const stopShare = async (
        typeStop: StopShareType,
        sharerId: string,
        recipientId?: string,
        resourceId?: string,
    ) => {
        setIsLoading(true);

        try {
            await documentsApi.stopShare({
                typeStop,
                recipientId,
                resourceId,
                targetSharerId: sharerId,
            });

            setUpdateData(true);

            const deletedListShareItem = getDeletedShareItems({
                typeStop,
                sharerId,
                recipientId,
                resourceId,
                shareItems: internalListData.listShareItem,
            });

            setInternalListData(getListDataAfterShareItemsDelete(internalListData, deletedListShareItem));
            return true;
        } catch (error) {
            console.error('Failed to stop sharing:', error);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const showStopShareConfirm = (typeStop: StopShareType, row: ManagerShareRow) => {
        const config = getStopShareConfig(typeStop, row);

        setPendingStopShareAction({
            ...config,
            typeStop,
            row,
        });
    };

    const handleConfirmStopShare = async () => {
        if (!pendingStopShareAction) {
            return;
        }

        const isStopped = await stopShare(
            pendingStopShareAction.typeStop,
            pendingStopShareAction.row.sharerId,
            pendingStopShareAction.recipientId,
            pendingStopShareAction.resourceId,
        );

        if (isStopped) {
            setPendingStopShareAction(null);
        }
    };

    const getActionMenuItems = (row: ManagerShareRow): MenuProps => ({
        items: [
            {
                key: 'recipient-resource',
                label: 'Stop this share only',
                danger: true,
            },
            {
                type: 'divider',
            },
            {
                key: 'recipient',
                label: 'Stop all shares to recipient',
                danger: true,
            },
            {
                key: 'resource',
                label: 'Stop resource for all recipients',
                danger: true,
            },
        ],
        onClick: ({ key, domEvent }) => {
            domEvent.preventDefault();
            domEvent.stopPropagation();
            showStopShareConfirm(key as StopShareType, row);
        },
    });

    const columns: ColumnType<ManagerShareRow>[] = [
        {
            title: 'Shared by',
            dataIndex: 'sharerContact',
            key: 'sharerContact',
            width: 180,
            ellipsis: true,
            filters: sharerFilters,
            filterSearch: true,
            onFilter: (value, row) => row.sharerId === value,
        },
        {
            title: 'Recipient',
            dataIndex: 'recipientContact',
            key: 'recipientContact',
            width: 180,
            ellipsis: true,
            filters: recipientFilters,
            filterSearch: true,
            onFilter: (value, row) => row.recipientId === value,
        },
        {
            title: 'Resource',
            dataIndex: 'resourceName',
            key: 'resourceName',
            width: 260,
            ellipsis: true,
            filterIcon: (filtered) => <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />,
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
                <div style={{ padding: 8, width: 260 }} onKeyDown={(event) => event.stopPropagation()}>
                    <Input
                        allowClear
                        autoFocus
                        placeholder="Search resource"
                        value={String(selectedKeys[0] ?? '')}
                        onChange={(event) => setSelectedKeys(event.target.value ? [event.target.value] : [])}
                        onPressEnter={() => confirm()}
                    />
                    <Space style={{ marginTop: 8 }}>
                        <Button type="primary" size="small" onClick={() => confirm()}>
                            Search
                        </Button>
                        <Button
                            size="small"
                            onClick={() => {
                                clearFilters?.();
                                confirm();
                            }}
                        >
                            Reset
                        </Button>
                    </Space>
                </div>
            ),
            onFilter: (value, row) => row.resourceName.toLowerCase().includes(String(value).trim().toLowerCase()),
        },
        {
            title: 'Type',
            dataIndex: 'resourceType',
            key: 'resourceType',
            width: 100,
            filters: [
                { text: 'Document', value: 'Document' },
                { text: 'Folder', value: 'Folder' },
            ],
            onFilter: (value, row) => row.resourceType === value,
            render: (resourceType: ManagerShareRow['resourceType']) => (
                <Tag color={resourceType === 'Folder' ? 'gold' : 'blue'}>{resourceType}</Tag>
            ),
        },
        {
            title: 'Shared at',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 170,
            render: formatEpochSeconds,
        },
        {
            title: 'Expires at',
            dataIndex: 'dueDate',
            key: 'dueDate',
            width: 170,
            render: formatEpochSeconds,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 130,
            align: 'center',
            render: (_, row) => (
                <Dropdown menu={getActionMenuItems(row)} trigger={['click']}>
                    <Button size="small" type="default">
                        Manage <DownOutlined />
                    </Button>
                </Dropdown>
            ),
        },
    ];

    return (
        <>
            <Spin spinning={isLoading}>
                <Table
                    size="small"
                    rowKey="key"
                    columns={columns}
                    dataSource={rows}
                    pagination={{ defaultPageSize: 10, showSizeChanger: true }}
                    scroll={{ x: 1180 }}
                    tableLayout="fixed"
                />
            </Spin>
            <Modal
                title={pendingStopShareAction?.title}
                open={Boolean(pendingStopShareAction)}
                okText="Stop sharing"
                okButtonProps={{ danger: true }}
                confirmLoading={isLoading}
                cancelText="Cancel"
                zIndex={2200}
                onOk={handleConfirmStopShare}
                onCancel={() => setPendingStopShareAction(null)}
            >
                <p>{pendingStopShareAction?.description}</p>
            </Modal>
        </>
    );
};

const getStopShareConfig = (typeStop: StopShareType, row: ManagerShareRow): StopShareConfig => {
    switch (typeStop) {
        case 'recipient':
            return {
                title: 'Stop recipient shares?',
                description: `Stop all shares from ${row.sharerContact} to ${row.recipientContact}.`,
                recipientId: row.recipientId,
            };
        case 'resource':
            return {
                title: 'Stop resource shares?',
                description: `Stop sharing ${row.resourceName} from ${row.sharerContact} to all recipients.`,
                resourceId: row.resourceId,
            };
        case 'recipient-resource':
            return {
                title: 'Stop this share?',
                description: `Stop sharing ${row.resourceName} from ${row.sharerContact} to ${row.recipientContact}.`,
                recipientId: row.recipientId,
                resourceId: row.resourceId,
            };
    }
};

const getDeletedShareItems = ({
    typeStop,
    sharerId,
    recipientId,
    resourceId,
    shareItems,
}: {
    typeStop: StopShareType;
    sharerId: string;
    recipientId?: string;
    resourceId?: string;
    shareItems: ShareItemRecord[];
}) => {
    switch (typeStop) {
        case 'recipient':
            return shareItems.filter((shareItem) => shareItem.sid === sharerId && shareItem.rid === recipientId);
        case 'resource':
            return shareItems.filter((shareItem) => shareItem.sid === sharerId && shareItem.id === resourceId);
        case 'recipient-resource':
            return shareItems.filter(
                (shareItem) =>
                    shareItem.sid === sharerId && shareItem.rid === recipientId && shareItem.id === resourceId,
            );
    }

    return [];
};
